import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { MEMBER_TYPE_STRING_CAPITAL, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { validateField } from "src/modules/validation"
import { InvitationService } from "src/openapi"
import { ShowHeader } from "src/states/showHeader"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Button, Typography } from "src/UILibrary"
import * as yup from "yup"

export const UserInvitePage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { accessToken, checkLoginStatus } = useLoginContext()

  const [userType, setUserType] = useState<string>(MEMBER_TYPE_STRING_CAPITAL.USER)
  const [email, setEmail] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  const validate = async () => {
    let validation = true

    try {
      await validateField(
        yup.object().shape({
          email: yup
            .string()
            .required(t("please_input_email"))
            .email(t("please_input_correct_email")),
        }),
        "email",
        email
      )
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      validation = validation && false
    }

    return validation
  }

  const handleInvite = async () => {
    if (!(await validate())) {
      return false
    }

    setShowProgress(true)
    InvitationService.createInvitation(accessToken, {
      email: email,
      userType: userType,
      comment: "INVITATION",
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({ show: true, text: t("success_invite"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed_invite"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_invite"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Box sx={{ px: "20px", mt: "20px" }}>
        <Typography.Large
          color="textPrimary"
          sx={{
            mb: "40px",
          }}
        >
          {t("invite_user")}
        </Typography.Large>
        <Box maxWidth="md">
          <InputTextField
            name={t("email")}
            required
            text={email}
            handleText={setEmail}
            validationMessage={emailValidationMessage}
            maxLength={50}
          ></InputTextField>
          <SelectField
            name={t("member_type")}
            required
            selected={userType}
            handleSelected={setUserType}
            datas={[
              {
                label: t("user"),
                value: MEMBER_TYPE_STRING_CAPITAL.USER,
              },
              {
                label: t("adviser"),
                value: MEMBER_TYPE_STRING_CAPITAL.ADVISER,
              },
              {
                label: t("company"),
                value: MEMBER_TYPE_STRING_CAPITAL.COMPANY,
              },
              {
                label: t("administrator"),
                value: MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR,
              },
            ]}
            sx={{ mt: "20px" }}
            inputStyle={{ width: "150px" }}
          />

          <Button
            variant="contained"
            sx={{
              minWidth: "240px",
              py: "16px",
              px: "88px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              fontWeight: 700,
              my: "40px",
              mx: "auto",
              display: "flex",
              justifyContent: "center",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handleInvite()}
          >
            {t("send")}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
